/**
 * La lectura del DNI —fotos, cascada de lectura, tapado del Nº de Trámite,
 * calidad y entrega a SADAIC— sin pantalla.
 *
 * Es el código que tenía adentro el diálogo del DNI, movido tal cual para que
 * lo usen dos pantallas: el diálogo (formulario interno) y la validación en
 * pasos de la pantalla pública. Cada una dibuja lo suyo con este mismo estado.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadMrzEngine, loadTesseractEngine, MRZ_WHITELIST, OcrEngine, OcrWord } from './tesseractLoader';
import {
  fileToCanvas,
  mrzStripCanvas,
  topHalfCanvas,
  cuilBandCanvas,
  rotate180,
  cloneCanvas,
  drawDataRegions,
  DataRegion,
  redactRegions,
  canvasToJpegBase64,
  RedactRect,
  apilarCaras,
} from './imagePrep';
import { extractMrz, MrzTd1Data } from './mrzTd1';
import {
  leerDocumento,
  lienzoDe,
  adaptadoresDelNavegador,
  CampoLeido,
  Hallazgo,
  LienzoCanvas,
  separarCaras,
  Separacion,
  ValorEsperado,
  consolidar,
  medirCara,
  motivosDe,
  veredictoGeneral,
  zonasATapar,
  LegibilidadDeZona,
  ModeloDeDocumento,
  EsquinasDeTarjeta,
  hallazgosDelMrz,
  CaraDeLaFoto,
  Rectangulo,
} from './reader';
import { cargarDecodificador, DecodificadorDeCodigos } from './zxingLoader';
import {
  AuthorRef,
  DniRejectReason,
  dniFromTaxId,
  validateAnverso,
  validateDorso,
  sexoEnElFrente,
  nacionalidadEnElFrente,
} from './dniMatch';
import {
  datosDesdeDni,
  ensamblar,
  DatosExtraidos,
  OrigenDeCampo,
  resolverDomicilio,
  OpcionRef,
} from './dniExtract';
import { cuitParaDni, revisarCuitCargado } from './cuit';
import { tokenManager } from '../../../api/tokenManager';
import { textosDni } from './textos';
import type { IdiomaDni } from './textos';
import { valoresDeLaLectura } from './valoresDeLaLectura';
import { recortarCodigo } from './recorteDelCodigo';
import { dniVencido } from './vencimiento';
import { aceptarCaras } from './aceptacion';
import { loQueTapaElDorso, loQueTapaElFrente, mismoDniDebeConfirmarse, zonasQueSeTapanEnElFrente } from './reglasDeLaFoto';

/**
 * Los campos consolidados, en la forma que espera el formulario.
 *
 * La cascada devuelve cada campo con sus fuentes y su verificación; el
 * formulario solo quiere valores y saber cuáles están probados.
 */
function datosDeLaLectura(campos: Record<string, CampoLeido>): DatosExtraidos {
  const verificados: string[] = [];
  const origenes: Record<string, OrigenDeCampo> = {};
  const d: DatosExtraidos = { verificados, origenes };
  const poner = (campo: keyof DatosExtraidos, desde: string) => {
    const c = campos[desde];
    if (!c?.valor) return;
    Object.assign(d, { [campo]: c.valor });
    if (c.verificacion === 'digito_de_control') verificados.push(String(campo));
    origenes[String(campo)] = c.fuentes[0]?.origen === 'mrz' ? 'mrz' : 'frente';
  };
  poner('lastName', 'apellido');
  poner('firstName', 'nombres');
  poner('dateOfBirth', 'fechaNacimiento');
  const sexo = campos.sexo?.valor;
  if (sexo === 'F' || sexo === 'M') {
    d.gender = sexo === 'F' ? 'female' : 'male';
    // El sexo puede estar PROBADO o no según de dónde venga, y hay que
    // propagarlo: el código de barras lo lleva con corrección de errores, pero
    // el verificador compuesto del MRZ saltea su posición. Se perdía ese estado
    // y el formulario mostraba "sin verificar" un dato que el código probaba.
    if (campos.sexo!.verificacion === 'digito_de_control') verificados.push('gender');
    origenes.gender = campos.sexo!.fuentes[0]?.origen === 'mrz' ? 'mrz' : 'frente';
  }
  const documento = campos.numeroDocumento?.valor;
  if (documento) {
    d.taxIdType = 'du';
    d.taxId = documento;
    if (campos.numeroDocumento.verificacion === 'digito_de_control') verificados.push('taxId');
    origenes.taxId = campos.numeroDocumento.fuentes[0]?.origen === 'mrz' ? 'mrz' : 'frente';
  }
  return d;
}

/** Lo que sabemos de la geometría de una cara, que es de lo que depende que el
 *  tapado del Nº de Trámite sea una garantía o una apuesta. */
interface GeometriaDeCara {
  tarjetaUbicada: boolean;
  /** Las esquinas de la tarjeta en la imagen que se entrega: de ellas sale la plantilla. */
  tarjeta: EsquinasDeTarjeta | null;
  /** El Nº de Trámite impreso, donde lo encontró el OCR buscando el número del código. */
  tramite: RedactRect | null;
  anchoTarjetaPx: number | null;
  modelo: ModeloDeDocumento | null;
}
const SIN_GEOMETRIA: GeometriaDeCara = { tarjetaUbicada: false, tarjeta: null, tramite: null, anchoTarjetaPx: null, modelo: null };

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/graphql', '') || 'http://localhost:4000';

/**
 * El server rechaza con un código, no con un texto. Decirle "intente en unos
 * minutos" a quien tiene un desajuste de datos lo manda a reintentar algo que
 * nunca va a andar: cada causa tiene su mensaje y, cuando hay algo que hacer,
 * lo dice.
 */
const ERRORES: Record<string, string> = {
  data_mismatch: 'e_data_mismatch',
  mrz_invalid: 'e_mrz_invalid',
  bad_image: 'e_bad_image',
  too_large: 'e_too_large',
  rate_limited: 'e_rate_limited',
  expired: 'e_link_expired',
  revoked: 'e_link',
  not_found: 'e_link',
  invalid_signature: 'e_link',
  no_data_validation: 'e_link',
};

function mensajeDelServidor(codigo: unknown, T: Record<string, string>): string {
  const clave = typeof codigo === 'string' ? ERRORES[codigo] : undefined;
  return (clave && T[clave]) || T.upload_failed;
}

/**
 * A dónde se manda la foto. Son dos caminos con credenciales distintas:
 *
 *  - `publico`: la página de validación del autor, autenticada por el vid+sig
 *    del link que le llegó por mail. No hay sesión.
 *  - `admin`: el formulario de la aplicación, autenticado por la sesión de
 *    quien está adentro, contra el autor que está editando.
 *  - `null`: no hay dónde mandarla todavía. Es el alta: el autor no existe, así
 *    que no hay a quién atribuirle la foto. Leer el documento y completar el
 *    formulario sí se puede; enviar, no.
 */
export type DestinoDni =
  | { tipo: 'publico'; vid: string; sig: string }
  | { tipo: 'admin'; authorComposerId: string }
  | null;

/**
 * El destino, traducido a una llamada HTTP. Lo único que cambia entre los dos
 * caminos es a qué URL se postea y con qué credencial: todo lo de abajo —el
 * staging cifrado, el gate OCR, la entrega— es el mismo código.
 */
async function destinoHttp(
  destino: Exclude<DestinoDni, null>
): Promise<{ url: string; credencial: Record<string, string>; cabeceras: Record<string, string> }> {
  if (destino.tipo === 'publico') {
    return {
      url: `${BASE_URL}/api/public/dni-upload`,
      credencial: { vid: destino.vid, sig: destino.sig },
      cabeceras: {},
    };
  }
  const token = await tokenManager.getValidAccessToken();
  return {
    url: `${BASE_URL}/api/dni-upload`,
    credencial: { authorComposerId: destino.authorComposerId },
    cabeceras: token ? { Authorization: `Bearer ${token}` } : {},
  };
}

/** Los datos del autor tal como están en el formulario, contra los que se compara el documento. */
export interface AutorDelFormulario {
  firstName: string;
  lastName: string;
  taxId: string;
  taxIdType: string;
  dateOfBirth: string;
  /** "male" | "female" | "nonBinary" — para poder comparar contra el documento. */
  gender?: string;
}

export type SideStatus = 'empty' | 'processing' | 'read' | 'error';

/** Una lectura del frente: la foto en una orientación y lo que el OCR sacó. */
interface LecturaFrente {
  canvas: HTMLCanvasElement;
  text: string;
  words: OcrWord[];
}

export const PREVIEW_WIDTH = 440;

/**
 * Cuánto se espera la respuesta de la entrega. El server responde recién
 * después de archivar la foto y de leerla, y la lectura tarda hasta 25 s: tiene
 * que quedar guardada antes de que llegue la confirmación de los datos, que
 * el cliente manda enseguida. El plazo cuenta además la subida de la foto, que
 * en base64 pesa hasta unos 2,8 MB y en un enlace lento lleva su tiempo. Si
 * fuera más corto, el cliente cortaría una entrega que el server ya archivó: el
 * autor vería una falla y, al reintentar, le volvería a pasar. El plazo solo
 * existe para liberar la pantalla ante una red colgada: vencido, se corta el
 * pedido y se dice como cualquier otra falla de red, y se puede reintentar.
 *
 * Se cambia junto con el «dos minutos» de `entregandoFoto` en los textos de la
 * validación en pasos (authorValidation/textos.ts), que le dice al autor cuánto
 * puede tardar: una prueba de esos textos los ata.
 */
export const PLAZO_DE_ENTREGA_MS = 120_000;

export interface ParametrosLecturaDni {
  /** Pasar de cerrado a abierto empieza de cero. La pantalla en pasos pasa siempre true. */
  open: boolean;
  destino: DestinoDni;
  author: AutorDelFormulario;
  onUploaded: () => void;
  /** Los catálogos por entidad (Country, ProvinceState), para resolver el domicilio. */
  lookups?: Record<string, OpcionRef[]>;
  /** Si ya hay un domicilio cargado (define si se incluye el del documento). */
  domicilioCargado?: boolean;
  onExtracted?: (valores: Record<string, any>, domicilioSinRepartir?: string) => void;
  idioma: IdiomaDni;
}

export function useLecturaDni({
  open,
  destino,
  author,
  onUploaded,
  lookups,
  domicilioCargado,
  onExtracted,
  idioma,
}: ParametrosLecturaDni) {
  const T = textosDni(idioma);
  const reversoCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewAnversoRef = useRef<HTMLCanvasElement | null>(null);
  const previewReversoRef = useRef<HTMLCanvasElement | null>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  // Calidad de cada foto, medida SIN OCR. Es una medida aparte a propósito: el
  // OCR puede sacar el MRZ entero de una foto que una persona no puede leer, y
  // puede no sacar nada de una que se lee perfecto. Avisa, nunca bloquea: lo
  // único que impide enviar es que falte nombre, número o fecha.
  const [calidadAnverso, setCalidadAnverso] = useState<LegibilidadDeZona[]>([]);
  const [calidadReverso, setCalidadReverso] = useState<LegibilidadDeZona[]>([]);
  /** Los hallazgos de la cascada: traen la caja de cada campo. */
  const [hallazgosAnverso, setHallazgosAnverso] = useState<Hallazgo[]>([]);
  const [hallazgosReverso, setHallazgosReverso] = useState<Hallazgo[]>([]);
  /**
   * Lo que vio el OCR sobre el impreso del frente.
   *
   * Se muestra SOLO cuando falta alguno de los datos que el frente tendría que
   * dar. Sin esto, un campo que falta es indistinguible de un campo que no
   * estaba, y no hay forma de saber si el problema fue la foto, el recorte o
   * que el rótulo no se leyó. Es el mismo criterio que ya rige para el dorso.
   */
  const [renglonesAnverso, setRenglonesAnverso] = useState<string[]>([]);
  /**
   * El lugar del código 2D del que salió el número de trámite.
   *
   * Va aparte de la plantilla porque no depende de ella: es el símbolo que se
   * acaba de decodificar. Tapar el número impreso y dejar el código que lo
   * contiene es no taparlo — y el impreso se puede tapar solo, con una cinta,
   * mientras el código sigue ahí entero.
   */
  const [zonasSensiblesAnverso, setZonasSensiblesAnverso] = useState<RedactRect[]>([]);
  /**
   * El corte PROPUESTO, esperando que la persona diga que sí.
   *
   * Partir solo sería adivinar sobre la foto de otro. La detección es buena
   * pero no infalible, y equivocarse acá tiene un costo claro: se leen dos
   * mitades de una tarjeta sola y no se lee nada. Se muestra dónde se cortaría
   * y se pregunta.
   */
  const [propuesta, setPropuesta] = useState<{
    lado: 'anverso' | 'reverso';
    partida: Separacion<LienzoCanvas>;
    original: HTMLCanvasElement;
  } | null>(null);
  const propuestaRef = useRef<HTMLCanvasElement | null>(null);
  /**
   * Qué cara reconoció la lectura en cada foto (caraDeLaFoto), y la foto tal como llegó: si en un
   * paso llega la otra cara, se puede leer de nuevo como tal (usarComoLaOtraCara).
   */
  const [caraReconocida, setCaraReconocida] = useState<{ anverso: CaraDeLaFoto | null; reverso: CaraDeLaFoto | null }>({
    anverso: null,
    reverso: null,
  });
  const originales = useRef<{ anverso: HTMLCanvasElement | null; reverso: HTMLCanvasElement | null }>({
    anverso: null,
    reverso: null,
  });
  const [geoAnverso, setGeoAnverso] = useState<GeometriaDeCara>(SIN_GEOMETRIA);
  const [geoReverso, setGeoReverso] = useState<GeometriaDeCara>(SIN_GEOMETRIA);
  const [anversoStatus, setAnversoStatus] = useState<SideStatus>('empty');
  const [reversoStatus, setReversoStatus] = useState<SideStatus>('empty');
  const [anversoError, setAnversoError] = useState('');
  const [reversoError, setReversoError] = useState('');
  const [lecturas, setLecturas] = useState<LecturaFrente[]>([]);
  const [mrz, setMrz] = useState<MrzTd1Data | null>(null);
  /**
   * Lo que aportó cada lectura a los datos, en el orden en que llegó, con su cara.
   *
   * Los datos se arman de acá y no se acumulan en un estado aparte: al reemplazar
   * o cancelar una cara se sacan sus aportes y lo de la foto vieja deja de
   * alimentar el domicilio, valoresLeidos y onExtracted. ensamblar() conserva lo
   * previo salvo que lo nuevo sea probado, así que acumular dejaba, por ejemplo,
   * el domicilio de un dorso reemplazado por otro que no lo deja leer.
   */
  const [aportes, setAportes] = useState<Array<{ lado: 'anverso' | 'reverso'; datos: DatosExtraidos }>>([]);
  const aportar = (lado: 'anverso' | 'reverso', nuevos: DatosExtraidos) =>
    setAportes((previos) => [...previos, { lado, datos: nuevos }]);
  const sacarAportes = (lado: 'anverso' | 'reverso') =>
    setAportes((previos) => (previos.some((a) => a.lado === lado) ? previos.filter((a) => a.lado !== lado) : previos));
  const datos = useMemo(
    () => aportes.reduce<DatosExtraidos | null>((previos, a) => ensamblar(previos, a.datos), null),
    [aportes]
  );
  const [extrayendo, setExtrayendo] = useState(false);
  const [completado, setCompletado] = useState(false);
  // Arranca apagado si ya hay un domicilio cargado: nunca se pisa en silencio
  // algo que alguien puso. Vacío, se completa sin tener que acordarse de tildar.
  const [incluirDomicilio, setIncluirDomicilio] = useState(!domicilioCargado);
  const [manualRects, setManualRects] = useState<RedactRect[]>([]);
  /** El tapado automático se puede revertir: la persona decide qué se envía. */
  const [tapadoAutomatico, setTapadoAutomatico] = useState(true);
  /**
   * La marca negra de cada cara, quitada o no por la persona en la pantalla en
   * pasos. Es una decisión sobre ESA foto: una foto nueva de la cara, o Cancelar,
   * la vuelve a poner (limpiarCara), y quitarla de una cara no destapa la otra.
   */
  const [marcaQuitada, setMarcaQuitada] = useState({ anverso: false, reverso: false });
  const ponerMarca = (lado: 'anverso' | 'reverso', puesta: boolean) =>
    setMarcaQuitada((previa) => ({ ...previa, [lado]: !puesta }));
  /** Si cada cara se entrega tapada (donde haya algo que tapar). */
  const marcaPuesta = {
    anverso: tapadoAutomatico && !marcaQuitada.anverso,
    reverso: tapadoAutomatico && !marcaQuitada.reverso,
  };
  const [camara, setCamara] = useState<'anverso' | 'reverso' | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  /**
   * Lo que no se pudo leer del frente, mostrado al apretar ENVIAR.
   *
   * No frena: avisa una vez y la segunda vez manda. Que la máquina no lea un
   * dato no quiere decir que la foto esté mal, y quien la sacó es quien puede
   * mirarla y decidir.
   */
  const [avisoEnvio, setAvisoEnvio] = useState<{ faltan: string[]; calidad: string[] } | null>(null);
  /**
   * Lo que se entregó (ver firmaDeLaEntrega), para saber si lo que se entregaría ahora es lo mismo.
   * null: no se entregó nada, o lo entregado ya no vale (otra foto).
   */
  const [entregada, setEntregada] = useState<{ fotos: Array<HTMLCanvasElement | null>; marcas: string } | null>(null);
  const [previewVersion, setPreviewVersion] = useState(0);
  /**
   * Una foto en proceso: decodificarla, ver si trae las dos caras y leerla.
   * Mientras dura, la pantalla espera y solo ofrece «Cancelar» (recorrido de
   * KAN-452).
   */
  const [procesando, setProcesando] = useState(false);
  /**
   * La lectura vigente de cada cara. Cada lectura toma su número al empezar y,
   * después de cada espera, sigue solo si todavía es la vigente: una foto nueva
   * o «Cancelar» la dejan atrás, y lo que termine después se descarta. Así una
   * lectura vieja nunca pisa a una nueva.
   */
  const vigente = useRef({ anverso: 0, reverso: 0 });
  /** La operación en curso (elegir, separar o no) y las caras que reemplaza. */
  const operacion = useRef<{ numero: number; lados: Array<'anverso' | 'reverso'> }>({ numero: 0, lados: [] });

  /**
   * Abrir el modal EMPIEZA DE CERO.
   *
   * El componente no se desmonta al cerrarse, así que sin esto la segunda
   * carga arranca con lo que dejó la primera: fotos, hallazgos, diagnósticos y
   * datos extraídos de OTRO documento. Peor que no leer es mostrar el dato de
   * antes como si fuera de la foto nueva.
   *
   * Lo que NO se borra es lo que vino de afuera —el autor, el destino— ni las
   * preferencias que dependen de él, que se recalculan desde sus props.
   */
  const estabaAbierto = useRef(false);
  useEffect(() => {
    // Solo al ABRIR. Si el efecto se volviera a disparar con el modal abierto
    // —porque cambió una prop— borraría la lectura en curso.
    const abriendo = open && !estabaAbierto.current;
    estabaAbierto.current = open;
    if (!abriendo) return;
    reversoCanvasRef.current = null;
    dragStart.current = null;
    setCalidadAnverso([]);
    setCalidadReverso([]);
    setHallazgosAnverso([]);
    setHallazgosReverso([]);
    setRenglonesAnverso([]);
    setZonasSensiblesAnverso([]);
    setPropuesta(null);
    setCaraReconocida({ anverso: null, reverso: null });
    originales.current = { anverso: null, reverso: null };
    setGeoAnverso(SIN_GEOMETRIA);
    setGeoReverso(SIN_GEOMETRIA);
    setAnversoStatus('empty');
    setReversoStatus('empty');
    setAnversoError('');
    setReversoError('');
    setLecturas([]);
    setMrz(null);
    setAportes([]);
    setExtrayendo(false);
    setCompletado(false);
    setIncluirDomicilio(!domicilioCargado);
    setManualRects([]);
    setTapadoAutomatico(true);
    setMarcaQuitada({ anverso: false, reverso: false });
    setCamara(null);
    setSending(false);
    setSendError('');
    setAvisoEnvio(null);
    setEntregada(null);
    setPreviewVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Los datos del autor tal como están AHORA en el formulario: si se completa
  // desde el documento, las comprobaciones de abajo se rehacen con lo nuevo.
  const dni = dniFromTaxId(author.taxId, author.taxIdType);
  const authorRef: AuthorRef = {
    dni: dni ?? '',
    lastName: author.lastName,
    firstName: author.firstName,
    dateOfBirth: author.dateOfBirth,
  };

  const verdictDorso = mrz ? validateDorso(mrz, authorRef) : null;

  // Cuál de las lecturas del frente sirve se decide ACÁ y no al cargar la foto:
  // la referencia puede haber llegado después (el dorso, o los datos completados
  // desde él), y girar una foto no se puede deshacer adivinando.
  const frente = useMemo(() => {
    if (lecturas.length === 0) return null;
    return lecturas.find((l) => validateAnverso(l.text, authorRef, mrz).ok) ?? lecturas[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lecturas, authorRef.dni, mrz]);
  const verdictAnverso = frente ? validateAnverso(frente.text, authorRef, mrz) : null;

  /**
   * Los campos consolidados de las dos caras juntas.
   *
   * Las filas tienen que mostrar ESTE estado y no un booleano: el consolidador
   * distingue cuatro situaciones —probado por dígito de control, coincidencia
   * entre fuentes independientes, lectura única y conflicto— y aplastarlas a
   * "verificado / sin verificar" perdía justamente el caso intermedio.
   */
  const camposTotales = useMemo(
    () => consolidar([...hallazgosAnverso, ...hallazgosReverso]),
    [hallazgosAnverso, hallazgosReverso]
  );

  /**
   * Lo que la OTRA cara ya probó, para que ésta pueda ubicarlo en la foto.
   *
   * La tarjeta tiene una identidad sola repartida en dos caras: el MRZ del
   * dorso prueba el nombre que está impreso en el frente. Sin pasárselo, cada
   * cara se lee sola y una cara cuyos rótulos el OCR no lee no puede señalar
   * nada aunque la otra lo haya probado todo.
   */
  const esperadosDe = (hallazgos: Hallazgo[]): ValorEsperado[] =>
    Object.entries(consolidar(hallazgos))
      .filter(([, c]) => !!c.valor)
      .map(([campo, c]) => ({ campo, valor: c.valor as string }));

  /**
   * Lo que va en la celda de un campo.
   *
   * Con desacuerdo entre fuentes el consolidador deja el valor en null —no
   * inventa un ganador—, y eso hacía desaparecer la fila entera: el apellido
   * simplemente no se mostraba. Un desacuerdo es información, no ausencia, así
   * que se muestran las dos lecturas y el estado dice que están en conflicto.
   */
  const textoDe = (campo: string, respaldo?: string | null): string | undefined => {
    const c = camposTotales[campo];
    if (c) {
      // Las lecturas alternativas se muestran SOLO cuando nada las decidió.
      //
      // Con una letra de diferencia —"LOVIRA" en el MRZ y "ROVIRA" impreso— el
      // consolidador elige una, y elegir en silencio esconde justo lo que hay
      // que mirar en la foto. Pero cuando un DÍGITO DE CONTROL ya decidió, la
      // otra lectura no es una alternativa: es una lectura descartada, y
      // ponerla al lado ensucia un dato probado. Medido: el código de barras
      // del frente dio "SOFIA" y el MRZ del dorso "SOFIAKLLLLLLLLLLLLRKLS", y
      // se mostraban los dos con el cartel de verificado.
      if (c.verificacion !== 'digito_de_control') {
        const leidas = Array.from(new Set(c.fuentes.map((f) => f.valorLeido).filter(Boolean)));
        if (leidas.length > 1) return leidas.join(' / ');
      }
      if (c.valor) return c.valor;
    }
    return respaldo ?? undefined;
  };

  /**
   * Los datos que el frente imprime en los tres formatos y no salieron.
   *
   * Es la lista que decide si mostrar lo que vio el OCR: mientras estén todos,
   * el detalle no le importa a nadie.
   */
  const faltanDelFrente = useMemo(() => {
    const esperados: [string, string][] = [
      ['apellido', T.rApellido],
      ['nombres', T.rNombre],
      ['numeroDocumento', T.rNumero],
      ['fechaNacimiento', T.rNacimiento],
    ];
    return esperados.filter(([campo]) => !camposTotales[campo]?.valor).map(([, r]) => r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camposTotales, T]);

  /**
   * El botón de copiar al formulario de UNA fila.
   *
   * Aparece solo cuando las tres cosas se cumplen a la vez: el dato está
   * VERIFICADO —probado por un dígito de control o coincidente entre dos
   * fuentes—, le corresponde a un campo del formulario, y DIFIERE de lo que hay
   * cargado. Si coincide no hay nada que copiar, y si no está verificado no
   * corresponde ofrecerlo con un botón: para eso está "completar", que pide una
   * revisión.
   */
  const soloAlfanum = (t: string) => t.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const estaVerificado = (campo: string) => {
    const v = camposTotales[campo]?.verificacion;
    return v === 'digito_de_control' || v === 'coincidencia_multiple';
  };

  const copiarSiDifiere = (
    campo: string,
    enElFormulario: string | undefined,
    valores: (valor: string) => Record<string, any>,
    /** Lo que se ESCRIBE, cuando no es el valor que se muestra (el CUIT). */
    aEscribir?: string | null
  ): (() => void) | undefined => {
    if (!onExtracted || !estaVerificado(campo)) return undefined;
    const leido = aEscribir ?? camposTotales[campo]?.valor;
    if (!leido) return undefined;
    if (soloAlfanum(enElFormulario ?? '') === soloAlfanum(leido)) return undefined;
    return () => onExtracted(valores(leido));
  };

  /**
   * El CUIT que le corresponde al documento leído.
   *
   * Sale del número más el sexo —el prefijo es 20 o 27— y de ahí su dígito
   * verificador. Es `null` cuando falta alguno de los dos: sin prefijo no hay
   * CUIT que calcular, y ofrecer uno inventado para un campo fiscal es peor que
   * no ofrecer nada.
   */
  const cuitDelDocumento = useMemo(() => {
    const numero = camposTotales.numeroDocumento?.valor;
    const sexo = datos?.gender;
    if (!numero) return null;
    return cuitParaDni(numero, sexo === 'male' || sexo === 'female' ? sexo : null);
  }, [camposTotales, datos]);

  /** 27391444 -> 27.391.444, para que se compare de un vistazo con el documento. */
  const formatearDni = (n: string) =>
    n.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  /**
   * Los motivos que comparan la foto contra los datos YA CARGADOS.
   *
   * En el alta no hay datos cargados: el registro todavía no existe —es lo
   * mismo que dice `destino`, que es null hasta que se guarda— y decir que la
   * foto "no coincide con sus datos" es acusar de contradecir a algo que no
   * está. Se callan.
   */
  const CONTRA_LO_CARGADO: DniRejectReason[] = [
    'dni_mismatch',
    'birth_mismatch',
    'name_mismatch',
    'anverso_numero_ajeno',
  ];
  const vieneAlCaso = (reason?: DniRejectReason) =>
    !!reason && (!!destino || !CONTRA_LO_CARGADO.includes(reason));

  /**
   * De todo lo que el frente puede no confirmar, solo DOS cosas ameritan un
   * cartel: que el número leído sea OTRO que el del dorso, o que sea otro que
   * el del formulario. Las dos dicen "este no es el documento que corresponde",
   * que es algo que la persona puede arreglar.
   *
   * Lo demás —que no se haya podido leer el número, que no haya contra qué
   * compararlo— no es un problema del documento: es que la máquina no llegó. Se
   * dice una vez al apretar enviar, con todo lo otro que no se pudo leer, y no
   * en un cartel amarillo por cada foto que se elige.
   */
  const ACCIONABLES: DniRejectReason[] = ['anverso_otro_documento', 'anverso_numero_ajeno'];

  const reasonMsg = (reason?: DniRejectReason, leido?: string) => {
    const base = reason ? T[reason] ?? T.image_error : T.image_error;
    // Cuando el documento es de otra persona, decir CUÁL número se leyó es la
    // diferencia entre "la foto está mal" y "este no es su documento".
    return base
      .replace('{leido}', formatearDni(leido ?? ''))
      .replace('{suyo}', formatearDni(authorRef.dni));
  };

  /**
   * Qué va a costarle leer a quien reciba la foto, y qué hacer para mejorarla
   * —que es más útil que "saque otra foto"—.
   *
   * No se muestra al cargar: nadie quiere un cartel amarillo por cada foto que
   * elige. Se dice UNA vez, al apretar enviar, junto con lo que no se pudo
   * leer, y ahí la persona decide. No impide nada.
   */
  const motivosDeCalidad = (zonas: LegibilidadDeZona[]): string[] => {
    const veredicto = veredictoGeneral(zonas);
    if (veredicto === 'legible' || veredicto === 'sin_medir') return [];
    return motivosDe(zonas).map((m) => T['m_' + m] ?? m);
  };

  // La imagen que se envía: copia de la original con el trámite tachado. Se
  // recalcula si aparece el número del titular (para no taparlo) o si la
  // persona agrega un tachado a mano.
  /** Qué se tapa por plantilla en el frente, y si eso alcanza para ser garantía. */
  const tapadoFrente = useMemo(
    () =>
      zonasATapar(
        // SIN MODELO RECONOCIDO NO SE TAPA NADA. Poner el formato intermedio
        // por defecto parecía inofensivo y no lo es: aplicó su franja —trámite
        // más código de barras— sobre un DNI de 2009, que no tiene ninguna de
        // las dos cosas en el frente, y le tapó la tercera línea del MRZ.
        //
        // Tapar por plantilla solo es una garantía cuando se sabe QUÉ
        // documento es. Con el modelo equivocado es destrucción de datos.
        geoAnverso.modelo,
        'frente',
        frente ? geoAnverso.tarjeta : null
      ),
    [geoAnverso, frente]
  );

  const tapadoDorso = useMemo(
    () =>
      zonasATapar(
        geoReverso.modelo,
        'dorso',
        reversoCanvasRef.current ? geoReverso.tarjeta : null
      ),
    // previewVersion es lo que avisa que el canvas del dorso cambió.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geoReverso, previewVersion]
  );

  /** El dorso que se envía: en el modelo 2026 el Nº de Trámite vive acá. */
  const reversoEnviable = useMemo(() => {
    const c = reversoCanvasRef.current;
    if (!c) return null;
    if (!marcaPuesta.reverso || tapadoDorso.zonas.length === 0) return c;
    const copia = cloneCanvas(c);
    redactRegions(copia, tapadoDorso.zonas.map((z) => z.caja));
    return copia;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tapadoDorso, previewVersion, marcaPuesta.reverso]);

  /**
   * Lo que se pinta de negro en el frente: solo lo que tiene ubicación cierta, la
   * plantilla garantizada y el código 2D decodificado (ver reglasDeLaFoto). El
   * tapado por el rótulo «Trámite» que leía el OCR se fue: era una apuesta y
   * pintaba líneas corridas.
   */
  const marcasDelFrente = useMemo(
    () => zonasQueSeTapanEnElFrente(tapadoFrente, zonasSensiblesAnverso, frente ? geoAnverso.tramite : null),
    [tapadoFrente, zonasSensiblesAnverso, frente, geoAnverso.tramite]
  );

  /**
   * Si el Nº de Trámite del frente quedó tapado de verdad en la foto que se
   * entrega: solo con la plantilla del modelo, que es la única que ubica el
   * número impreso con certeza. Tapar solo el código no alcanza: el número
   * impreso sigue a la vista, y decir «Tapamos» ahí es falso. Un modelo sin nada
   * que tapar en el frente (el 2026) no tapó nada: da falso.
   */
  const tramiteTapadoFrente = marcaPuesta.anverso && tapadoFrente.garantizado && tapadoFrente.zonas.length > 0;

  /**
   * Si cada cara lleva una marca negra (aunque la persona la haya quitado): es lo
   * que permite ofrecer quitarla o volver a ponerla.
   */
  const hayMarcaNegra = { anverso: marcasDelFrente.length > 0, reverso: tapadoDorso.zonas.length > 0 };

  /** Qué tapa la marca negra de cada cara (aunque se haya quitado): la pantalla pública lo dice. */
  const queTapaLaMarca = {
    anverso: loQueTapaElFrente(tapadoFrente, zonasSensiblesAnverso, frente ? geoAnverso.tramite : null),
    reverso: loQueTapaElDorso(tapadoDorso),
  };

  const anversoEnviable = useMemo(() => {
    if (!frente) return null;
    const copia = cloneCanvas(frente.canvas);
    // Por posición: la plantilla garantizada y el código decodificado. Quien la
    // envía puede quitar la marca (marcaPuesta) si cayó mal.
    if (marcaPuesta.anverso && marcasDelFrente.length > 0) redactRegions(copia, marcasDelFrente);
    if (manualRects.length > 0) redactRegions(copia, manualRects);
    return copia;
  }, [frente, manualRects, marcasDelFrente, marcaPuesta.anverso]);

  /**
   * Los recuadros que señalan qué se entendió de la foto.
   *
   * Salen de los HALLAZGOS de la cascada, que ya traen la caja de cada campo en
   * las coordenadas de la imagen recortada. Antes había un segundo camino que
   * volvía a buscar los mismos datos y los escalaba por su cuenta: con el
   * recorte nuevo, dividía las cajas por 2,56 y los carteles terminaban todos
   * amontonados arriba a la izquierda.
   */
  const marcas: DataRegion[] = useMemo(() => {
    const etiquetas: Record<string, string> = {
      apellido: T.rApellido,
      nombres: T.rNombre,
      numeroDocumento: T.rNumero,
      fechaNacimiento: T.rNacimiento,
      fechaVencimiento: T.rVencimiento,
      sexo: T.rSexo,
      nacionalidad: T.rNacionalidad,
    };
    return hallazgosAnverso
      .filter((h) => h.caja && etiquetas[h.campo])
      .map((h) => ({ label: etiquetas[h.campo], rect: h.caja! }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hallazgosAnverso, T]);

  const dibujar = (
    src: HTMLCanvasElement | null,
    dst: HTMLCanvasElement | null,
    recuadros?: DataRegion[]
  ) => {
    if (!src || !dst) return;
    const escala = PREVIEW_WIDTH / src.width;
    dst.width = PREVIEW_WIDTH;
    dst.height = Math.round(src.height * escala);
    dst.getContext('2d')!.drawImage(src, 0, 0, dst.width, dst.height);
    // Los recuadros van SOLO en la vista previa: la imagen que se envía no se
    // toca (salvo el tachado del trámite, que sí tiene que viajar quemado).
    if (recuadros && recuadros.length > 0) drawDataRegions(dst, recuadros, escala);
  };

  const refreshPreview = useCallback(() => {
    dibujar(anversoEnviable, previewAnversoRef.current, marcas);
    dibujar(reversoEnviable, previewReversoRef.current);
  }, [anversoEnviable, reversoEnviable, marcas]);

  useEffect(() => {
    refreshPreview();
  }, [previewVersion, anversoStatus, reversoStatus, refreshPreview]);

  /**
   * Borra en el acto todo lo de una cara que se reemplaza: imagen, lectura,
   * recuadros, avisos y tapado. Nada de la foto anterior queda a la vista
   * mientras se procesa la nueva, ni después si se cancela.
   */
  const limpiarCara = (lado: 'anverso' | 'reverso') => {
    vigente.current[lado] += 1;
    originales.current[lado] = null;
    setCaraReconocida((previa) => (previa[lado] ? { ...previa, [lado]: null } : previa));
    // Una cara nueva se vuelve a entregar: la pantalla deja volver a las fotos después de
    // entregarlas, y el server reemplaza la foto pendiente por la nueva.
    setEntregada(null);
    setMarcaQuitada((previa) => (previa[lado] ? { ...previa, [lado]: false } : previa));
    if (lado === 'anverso') {
      setAnversoStatus('empty');
      setAnversoError('');
      setCalidadAnverso([]);
      setGeoAnverso(SIN_GEOMETRIA);
      setHallazgosAnverso([]);
      setRenglonesAnverso([]);
      setZonasSensiblesAnverso([]);
      setLecturas([]);
      setManualRects([]);
      setAvisoEnvio(null);
      sacarAportes('anverso');
    } else {
      sacarAportes('reverso');
      reversoCanvasRef.current = null;
      setReversoStatus('empty');
      setReversoError('');
      setCalidadReverso([]);
      setGeoReverso(SIN_GEOMETRIA);
      setHallazgosReverso([]);
      setMrz(null);
      setExtrayendo(false);
      setPreviewVersion((v) => v + 1);
    }
  };

  /** Empieza una operación que reemplaza estas caras: las borra y espera. */
  const empezar = (lados: Array<'anverso' | 'reverso'>): number => {
    const numero = operacion.current.numero + 1;
    operacion.current = { numero, lados };
    for (const lado of lados) limpiarCara(lado);
    setProcesando(true);
    return numero;
  };
  const sigue = (numero: number) => operacion.current.numero === numero;
  const terminar = (numero: number) => {
    if (!sigue(numero)) return;
    operacion.current = { numero, lados: [] };
    setProcesando(false);
  };

  /**
   * «Cancelar»: lo que se estaba leyendo se descarta —el motor puede seguir un
   * rato, pero su resultado ya no entra— y las caras que se reemplazaban
   * quedan vacías. La foto anterior no vuelve.
   */
  const cancelar = () => {
    const { numero, lados } = operacion.current;
    operacion.current = { numero: numero + 1, lados: [] };
    for (const lado of lados) limpiarCara(lado);
    setPropuesta(null);
    setProcesando(false);
  };

  const tryDorso = async (
    engine: OcrEngine,
    canvas: HTMLCanvasElement
  ): Promise<MrzTd1Data | null> => {
    const { text } = await engine.recognize(mrzStripCanvas(canvas), { whitelist: MRZ_WHITELIST });
    return extractMrz(text);
  };

  /**
   * El dorso. Recibe la foto, o el recorte que salió de una foto con las dos
   * caras — de ahí que acepte un canvas y no solo un File.
   *
   * `esperados` va explícito porque cuando las dos caras salen de la MISMA foto
   * se leen una atrás de la otra sin que React haya re-renderizado en el medio:
   * leer el estado ahí devuelve lo de antes. Se pasa el dato, no se lo busca.
   */
  const handleReversoFile = async (
    entrada: File | HTMLCanvasElement,
    esperados?: ValorEsperado[],
    pista?: Rectangulo
  ): Promise<Hallazgo[]> => {
    const mia = ++vigente.current.reverso;
    const seguir = () => {
      if (vigente.current.reverso !== mia) throw new LecturaDescartada();
    };
    setReversoStatus('processing');
    setReversoError('');
    setCalidadReverso([]);
    setGeoReverso(SIN_GEOMETRIA);
    setMrz(null);
    // El dorso NO borra lo que dio el frente: se ensambla. Ponerlo en null era
    // el motivo de que cargar el dorso hiciera desaparecer apellido y nombres.
    setHallazgosReverso([]);
    let leidos: Hallazgo[] = [];
    try {
      const engine: OcrEngine = await loadTesseractEngine();
      const original = entrada instanceof HTMLCanvasElement ? entrada : await fileToCanvas(entrada);
      seguir();
      originales.current.reverso = original;

      let decodificador: DecodificadorDeCodigos | undefined;
      try {
        decodificador = await cargarDecodificador();
      } catch {
        decodificador = undefined;
      }

      // El modelo entrenado sobre el alfabeto del MRZ, igual de opcional: si no
      // baja, el MRZ se lee con el motor común y se pierde calidad, no
      // funcionalidad.
      let motorMrz: OcrEngine | undefined;
      try {
        motorMrz = await loadMrzEngine();
      } catch {
        motorMrz = undefined;
      }
      seguir();

      // La misma cascada que el frente: orientar, ubicar la tarjeta, recortar.
      // En el dorso el ancla es el MRZ —tres renglones largos y paralelos— o el
      // QR, según el modelo.
      const lectura = await leerDocumento(
        lienzoDe(original),
        'dorso',
        adaptadoresDelNavegador(engine, decodificador, motorMrz),
        undefined,
        esperados ?? esperadosDe(hallazgosAnverso),
        pista ? { pistaDeTarjeta: pista } : {}
      );
      seguir();
      let canvas = lectura.imagen.canvas;
      setCaraReconocida((previa) => ({ ...previa, reverso: lectura.caraReconocida ?? null }));
      const anchoTarjetaDorso = lectura.tarjetaUbicada ? canvas.width : null;
      setHallazgosReverso(lectura.hallazgos);

      leidos = lectura.hallazgos;

      let leido = await tryDorso(engine, canvas);
      seguir();
      if (!leido) {
        // Auxilio: la foto pudo venir al revés.
        const girada = rotate180(canvas);
        const reintento = await tryDorso(engine, girada);
        seguir();
        if (reintento) {
          canvas = girada;
          leido = reintento;
        }
      }
      reversoCanvasRef.current = canvas;
      setPreviewVersion((v) => v + 1);
      // Lo que la cascada leyó del dorso entra ensamblado, sin pisar al frente.
      aportar('reverso', datosDeLaLectura(lectura.campos));
      setMrz(leido);
      setReversoStatus(leido ? 'read' : 'error');
      if (!leido) setReversoError(T.mrz_ilegible);
      // Aunque el MRZ no valide se sigue: el domicilio y el CUIL están
      // impresos aparte y se leen igual. Decir "no pudimos leer el dorso" con
      // el domicilio entero a la vista es falso, y manda a sacar otra foto que
      // va a fallar por lo mismo. Enviar sigue sin habilitarse: eso lo decide
      // la regla dura, no esta pantalla.
      // El resto del dorso (CUIL y domicilio) vive en otras zonas y necesita su
      // propio preproceso. Va DESPUÉS de dar el dorso por leído: son dos
      // pasadas más de OCR y no hacen falta para habilitar el envío.
      // La tarjeta y el modelo ya se saben: van antes, así una falla de esas
      // pasadas no deja al dorso como «sin tarjeta», que pide otra foto.
      setGeoReverso({
        tarjetaUbicada: lectura.tarjetaUbicada,
        // Solo con ancla (ver tarjetaPorAncla), y si el dorso no se leyó girado 180° (el auxilio
        // de arriba): ahí las esquinas ya no valen.
        tarjeta: lectura.tarjetaPorAncla && canvas === lectura.imagen.canvas ? lectura.tarjetaEnLaImagen ?? null : null,
        tramite: null,
        anchoTarjetaPx: anchoTarjetaDorso,
        modelo: lectura.modelo,
      });
      setExtrayendo(true);
      try {
        const { text: banda } = await engine.recognize(cuilBandCanvas(canvas));
        seguir();
        // El domicilio está en el tercio de arriba de la TARJETA, pero acá se
        // recorta el tercio de arriba de la FOTO. Cuando la tarjeta no llena el
        // cuadro —sobra fondo arriba, o entró torcida— el recorte se queda
        // corto y el domicilio no aparece. Por eso, si con el recorte de
        // siempre no sale, se prueba uno más amplio antes de darlo por perdido:
        // cuesta una pasada más de OCR y solo cuando ya falló.
        let arriba = (await engine.recognize(topHalfCanvas(canvas))).text;
        seguir();
        let datos = datosDesdeDni(leido, `${arriba}\n${banda}`);
        if (!datos.domicilioLeido) {
          arriba = (await engine.recognize(topHalfCanvas(canvas, 0.8))).text;
          seguir();
          const conMasAire = datosDesdeDni(leido, `${arriba}\n${banda}`);
          if (conMasAire.domicilioLeido) datos = conMasAire;
        }
        aportar('reverso', datos);
        // Con la tarjeta ubicada las cajas del catálogo sí valen, y la medición
        // puede decir QUÉ zona está tapada por el reflejo en vez de solo que la
        // foto tiene reflejo.
        setCalidadReverso(
          // Las zonas del modelo y la escala, solo con la tarjeta anclada: ubicada por su contorno no
          // hay escala medida, y se mide la cara entera.
          medirCara(canvas, lectura.tarjetaPorAncla && canvas === lectura.imagen.canvas ? lectura.modelo : null, 'dorso', {
            anchoTarjetaPx: anchoTarjetaDorso,
            // Las zonas, sobre la tarjeta (no sobre el recorte); no si se leyó girado 180°.
            // Solo con ancla (el MRZ): es la que da una escala confiable.
            tarjeta: lectura.tarjetaPorAncla && canvas === lectura.imagen.canvas ? lectura.tarjetaEnLaImagen ?? null : null,
          })
        );
      } catch (e) {
        if (e instanceof LecturaDescartada) throw e;
        // Sin CUIL ni domicilio igual hay datos: los del MRZ, que son los
        // verificados. No es motivo para fallar. Salvo que esta lectura ya no sea
        // la vigente: sus datos no son del dorso que está ahora.
        seguir();
        aportar('reverso', datosDesdeDni(leido, ''));
        setCalidadReverso(medirCara(canvas, null, 'dorso'));
      } finally {
        if (vigente.current.reverso === mia) setExtrayendo(false);
      }
    } catch (e) {
      if (e instanceof LecturaDescartada) return [];
      setReversoStatus('error');
      setReversoError(T[(e as Error).message] ?? T.image_error);
    }
    return leidos;
  };

  /** El frente. Mismo trato que el dorso: File o recorte, y devuelve lo leído. */
  const handleAnversoFile = async (
    entrada: File | HTMLCanvasElement,
    esperados?: ValorEsperado[],
    pista?: Rectangulo
  ): Promise<Hallazgo[]> => {
    const mia = ++vigente.current.anverso;
    const seguir = () => {
      if (vigente.current.anverso !== mia) throw new LecturaDescartada();
    };
    setAnversoStatus('processing');
    setAnversoError('');
    setCalidadAnverso([]);
    setGeoAnverso(SIN_GEOMETRIA);
    setHallazgosAnverso([]);
    setRenglonesAnverso([]);
    setZonasSensiblesAnverso([]);
    setLecturas([]);
    setManualRects([]);
    setAvisoEnvio(null);
    let leidos: Hallazgo[] = [];
    try {
      const engine: OcrEngine = await loadTesseractEngine();
      const original = entrada instanceof HTMLCanvasElement ? entrada : await fileToCanvas(entrada);
      seguir();

      // El decodificador de códigos es opcional: si el CDN no responde, la
      // lectura sigue con lo que haya. No se cae por eso.
      let decodificador: DecodificadorDeCodigos | undefined;
      try {
        decodificador = await cargarDecodificador();
      } catch {
        decodificador = undefined;
      }

      // El modelo entrenado sobre el alfabeto del MRZ, igual de opcional: si no
      // baja, el MRZ se lee con el motor común y se pierde calidad, no
      // funcionalidad.
      let motorMrz: OcrEngine | undefined;
      try {
        motorMrz = await loadMrzEngine();
      } catch {
        motorMrz = undefined;
      }
      seguir();

      // La cascada: orientar, ubicar la tarjeta, recortar y leer. El original
      // NO se modifica: lo que vuelve es una imagen nueva.
      const lectura = await leerDocumento(
        lienzoDe(original),
        'frente',
        adaptadoresDelNavegador(engine, decodificador, motorMrz),
        undefined,
        esperados ?? esperadosDe(hallazgosReverso),
        pista ? { pistaDeTarjeta: pista } : {}
      );
      const canvas = lectura.imagen.canvas;
      originales.current.anverso = original;
      setCaraReconocida((previa) => ({ ...previa, anverso: lectura.caraReconocida ?? null }));
      const anchoTarjetaFrente = lectura.tarjetaUbicada ? canvas.width : null;

      seguir();
      const derecha = await engine.recognize(canvas);
      seguir();
      const leidas: LecturaFrente[] = [{ canvas, text: derecha.text, words: derecha.words }];
      setLecturas(leidas);
      setGeoAnverso({
        tarjetaUbicada: lectura.tarjetaUbicada,
        // Para tapar, solo la tarjeta ubicada por un ancla (el código, el MRZ), no por su contorno.
        tarjeta: lectura.tarjetaPorAncla ? lectura.tarjetaEnLaImagen ?? null : null,
        tramite: lectura.tramiteEnLaImagen ?? null,
        anchoTarjetaPx: anchoTarjetaFrente,
        modelo: lectura.modelo,
      });
      setHallazgosAnverso(lectura.hallazgos);
      setRenglonesAnverso(lectura.renglonesDelImpreso);
      setZonasSensiblesAnverso(lectura.zonasSensibles);
      leidos = lectura.hallazgos;
      // Lo que la cascada leyó entra al formulario como cualquier otra fuente.
      aportar('anverso', datosDeLaLectura(lectura.campos));
      setCalidadAnverso(
        // Las zonas del modelo y la escala, solo con la tarjeta anclada: ubicada por su contorno no
          // hay escala medida, y se mide la cara entera.
          medirCara(canvas, lectura.tarjetaPorAncla ? lectura.modelo : null, 'frente', {
          anchoTarjetaPx: anchoTarjetaFrente,
          // Las zonas, sobre la tarjeta ubicada y a su escala, no sobre el recorte con margen.
          tarjeta: lectura.tarjetaPorAncla ? lectura.tarjetaEnLaImagen ?? null : null,
        })
      );
      setAnversoStatus('read');
      setPreviewVersion((v) => v + 1);
    } catch (e) {
      if (e instanceof LecturaDescartada) return [];
      setAnversoStatus('error');
      setAnversoError(T[(e as Error).message] ?? T.image_error);
    }
    return leidos;
  };

  /**
   * La foto que llega, sea de una cara o de las dos.
   *
   * Mucha gente fotografía o escanea el documento entero: las dos caras en una
   * imagen. Antes eso entraba como frente, se leía UNA de las dos tarjetas y la
   * otra se perdía — y el modal seguía pidiendo el dorso que ya estaba ahí.
   *
   * Si la imagen trae las dos, se parte y se llenan los dos casilleros. El
   * dorso va PRIMERO: es el que trae el MRZ, y lo que prueba sirve para ubicar
   * los mismos datos impresos en el frente.
   */
  /** `pista`: el marco de la cámara, en fracciones de la foto (ver marcoDeLaTarjeta). */
  const handleFoto = async (lado: 'anverso' | 'reverso', file: File, pista?: Rectangulo) => {
    // La cara que se reemplaza se borra en el acto, y se espera hasta leer la nueva.
    const numero = empezar([lado]);
    setPropuesta(null);
    let partida: Separacion<LienzoCanvas> | null = null;
    // La foto se decodifica UNA vez: si no hay que partirla, la mano de esa
    // cara recibe el canvas y no el File.
    let original: HTMLCanvasElement | null = null;
    try {
      const engine: OcrEngine = await loadTesseractEngine();
      original = await fileToCanvas(file);
      partida = await separarCaras(lienzoDe(original), adaptadoresDelNavegador(engine));
      if (!sigue(numero)) return;
      if (partida) {
        // Se pregunta: la espera termina, y la cara sigue vacía hasta la respuesta.
        setPropuesta({ lado, partida, original });
        terminar(numero);
        return;
      }
    } catch {
      // Partir es una comodidad. Si algo falla —el motor no bajó, la imagen no
      // se pudo recortar— se sigue por el camino de siempre, que es el que
      // tiene el manejo de errores de verdad.
      partida = null;
    }
    if (!sigue(numero)) return;
    const entrada = original ?? file;
    // El marco de la cámara sirve para la foto tal como se sacó (no si se separó en dos caras).
    if (lado === 'anverso') await handleAnversoFile(entrada, undefined, pista);
    else await handleReversoFile(entrada, undefined, pista);
    terminar(numero);
  };

  /**
   * Sí: se parte. El dorso PRIMERO, que es el que trae el MRZ. Las dos caras se
   * reemplazan: se borran en el acto, también la que no es la del paso.
   */
  const aceptarCorte = async () => {
    const p = propuesta;
    setPropuesta(null);
    if (!p) return;
    const numero = empezar(['anverso', 'reverso']);
    const dorso = p.partida.caras.find((c) => c.cara === 'dorso');
    const frenteCara = p.partida.caras.find((c) => c.cara === 'frente');
    const delDorso = dorso ? await handleReversoFile(dorso.imagen.canvas, []) : [];
    if (frenteCara && sigue(numero)) await handleAnversoFile(frenteCara.imagen.canvas, esperadosDe(delDorso));
    terminar(numero);
  };

  /**
   * La foto que llegó en el paso de una cara es de la otra: se lee como la otra cara, y la de
   * este paso queda vacía (la otra, si tenía foto, se reemplaza).
   */
  const usarComoLaOtraCara = async (lado: 'anverso' | 'reverso') => {
    const original = originales.current[lado];
    if (!original) return;
    const otra = lado === 'anverso' ? 'reverso' : 'anverso';
    const numero = empezar([lado, otra]);
    if (otra === 'reverso') await handleReversoFile(original);
    else await handleAnversoFile(original);
    terminar(numero);
  };

  /**
   * Las dos fotos cruzadas (el dorso en el paso del frente y el frente en el del dorso): cada una
   * se lee como la otra cara, el dorso primero, como con una foto doble.
   */
  const intercambiarCaras = async () => {
    const { anverso, reverso } = originales.current;
    if (!anverso || !reverso) return;
    const numero = empezar(['anverso', 'reverso']);
    const delDorso = await handleReversoFile(anverso, []);
    if (sigue(numero)) await handleAnversoFile(reverso, esperadosDe(delDorso));
    terminar(numero);
  };

  /** Si la foto de cada paso es, según la lectura, la de la otra cara. */
  const caraEquivocada = {
    anverso: caraReconocida.anverso === 'dorso',
    reverso: caraReconocida.reverso === 'frente',
  };

  /** No: es una tarjeta sola y se lee entera, en el casillero que se eligió. */
  const rechazarCorte = async () => {
    const p = propuesta;
    setPropuesta(null);
    if (!p) return;
    const numero = empezar([p.lado]);
    if (p.lado === 'anverso') await handleAnversoFile(p.original);
    else await handleReversoFile(p.original);
    terminar(numero);
  };

  /** La foto entera con la línea por donde se cortaría. */
  useEffect(() => {
    const dst = propuestaRef.current;
    if (!propuesta || !dst) return;
    const src = propuesta.original;
    const escala = PREVIEW_WIDTH / src.width;
    dst.width = PREVIEW_WIDTH;
    dst.height = Math.round(src.height * escala);
    const ctx = dst.getContext('2d')!;
    ctx.drawImage(src, 0, 0, dst.width, dst.height);
    const en = propuesta.partida.corte.en * escala;
    ctx.strokeStyle = '#d32f2f';
    ctx.lineWidth = 3;
    ctx.setLineDash([10, 6]);
    ctx.beginPath();
    if (propuesta.partida.corte.vertical) {
      ctx.moveTo(0, en);
      ctx.lineTo(dst.width, en);
    } else {
      ctx.moveTo(en, 0);
      ctx.lineTo(en, dst.height);
    }
    ctx.stroke();
  }, [propuesta]);

  // Redacción MANUAL: arrastrar un rectángulo sobre la vista previa lo agrega a
  // los tachados que se queman en la imagen que se envía.
  const previewPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const onPreviewDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    dragStart.current = previewPoint(e);
  };
  const onPreviewUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const start = dragStart.current;
    dragStart.current = null;
    const src = frente?.canvas;
    if (!start || !src) return;
    const end = previewPoint(e);
    const escala = src.width / PREVIEW_WIDTH;
    const r: RedactRect = {
      x: Math.min(start.x, end.x) * escala,
      y: Math.min(start.y, end.y) * escala,
      w: Math.abs(end.x - start.x) * escala,
      h: Math.abs(end.y - start.y) * escala,
    };
    if (r.w < 4 || r.h < 4) return;
    setManualRects((prev) => [...prev, r]);
  };

  // El domicilio repartido en los campos, hasta donde el catálogo lo respalde.
  const domicilio = useMemo(
    () => (datos?.domicilioLeido ? resolverDomicilio(datos.domicilioLeido, lookups ?? {}) : {}),
    [datos, lookups]
  );
  // El CUIT cargado, mirado contra el documento. El campo puede venir como
  // CUIT/CUIL o como DNI: un DNI correcto no es un error, es algo a completar.
  // El frente como SEGUNDA lectura de lo que el MRZ no puede verificar. No es
  // un dígito de control, pero dos zonas distintas diciendo lo mismo ya no es
  // una lectura suelta. La ausencia no contradice: el frente casi no se lee.
  const sexoConfirmado =
    !!mrz?.sex && !!frente && sexoEnElFrente(frente.words) === mrz.sex;
  const nacionalidadConfirmada =
    !!frente && nacionalidadEnElFrente(frente.text, mrz?.nationality ?? null);

  // Vencido se calcula por fecha, no por el documento: el DNI no lo dice.
  /** El vencimiento consolidado: puede venir del MRZ de cualquiera de las caras. */
  const vencimiento = camposTotales.fechaVencimiento?.valor ?? mrz?.expiryDate ?? null;
  // Hoy es el día en la zona del proyecto, no el día UTC (ver vencimiento.ts).
  const vencido = dniVencido(vencimiento);

  /**
   * El CUIT cargado, mirado contra el NÚMERO CONSOLIDADO.
   *
   * Salía del MRZ crudo, que es una lectura entre varias y no siempre la mejor:
   * medido sobre un DNI real, el MRZ leyó 29861034 donde el código de barras
   * probaba 29861304, y la pantalla ofrecía el CUIT de un número que no existe.
   * El consolidado es el mismo que muestra la tabla y del que sale el CUIT
   * calculado: no puede haber dos números distintos en la misma pantalla.
   */
  const revisionCuit = useMemo(
    () =>
      revisarCuitCargado(
        author.taxId,
        camposTotales.numeroDocumento?.valor ?? mrz?.documentNumber ?? '',
        datos?.gender === 'male' || datos?.gender === 'female' ? datos.gender : null
      ),
    [author.taxId, camposTotales, mrz, datos]
  );

  const nombreDeFila = (entidad: string, id?: string) =>
    id ? lookups?.[entidad]?.find((o) => o.id === id)?.displayId : undefined;

  /** Lo leído, en la forma del formulario (ver valoresDeLaLectura). */
  const valoresLeidos = () =>
    valoresDeLaLectura({
      camposTotales,
      datos,
      nacionalidadMrz: mrz?.nationality,
      domicilio,
      incluirDomicilio,
    });

  const completar = () => {
    if (!datos || !onExtracted) return;
    const { valores, domicilioSinRepartir } = valoresLeidos();
    onExtracted(valores, domicilioSinRepartir);
    setCompletado(true);
  };

  // Alcanza con que las dos fotos estén cargadas y verificadas. Tapar el Nº de
  // Trámite es una sugerencia —ya se tapa solo— y no una condición: dejar el
  // envío colgado de un tilde frenaba a gente que tenía todo bien.
  /**
   * Qué falta para poder enviar. Se enumera en vez de dejar el botón apagado y
   * mudo: un botón deshabilitado sin explicación obliga a adivinar, y acá lo
   * que falta puede ser una foto que la persona cree haber cargado.
   */
  /**
   * Para enviar alcanza con las DOS FOTOS. Lo que el OCR haya podido leer no
   * decide si se manda: SADAIC pide la foto del documento, y una foto que una
   * persona lee perfecto puede no dejarse leer por una máquina. Bloquear por
   * eso deja al autor sin poder mandar lo que tiene.
   *
   * Lo que sí se hace es AVISAR: al apretar, si alguno de los cuatro datos del
   * frente no se pudo leer, se dice cuáles y se pide apretar de nuevo. Avisar y
   * dejar decidir, no impedir.
   */
  /**
   * Si la foto que se entregaría ahora es la que ya se entregó. Cualquier cambio de la imagen a
   * entregar —otra foto, poner o quitar la marca negra, un tachado a mano— la deja por entregar
   * otra vez (revisión del recorrido 2): si no, la pantalla mostraría una foto y el server tendría
   * otra.
   */
  const firmaDeLaEntrega = {
    // Las fotos de cada cara y todo lo que se pinta encima.
    fotos: [frente?.canvas ?? null, reversoCanvasRef.current],
    marcas: JSON.stringify([
      marcaPuesta.anverso ? marcasDelFrente : [],
      manualRects,
      marcaPuesta.reverso ? tapadoDorso.zonas.map((z) => z.caja) : [],
    ]),
  };
  const enviado =
    !!entregada &&
    entregada.fotos.every((f, i) => f === firmaDeLaEntrega.fotos[i]) &&
    entregada.marcas === firmaDeLaEntrega.marcas;

  const canSend = !!destino && !!anversoEnviable && !!reversoEnviable && !sending && !enviado;

  /**
   * Si las dos caras se aceptan (ver aceptacion.ts): datos confiables, las dos
   * del mismo documento y los datos básicos legibles en alguna. `null` hasta
   * tener las dos leídas, con sus datos extra (CUIL, domicilio) ya terminados.
   */
  const aceptacion = useMemo(() => {
    const leidas =
      !!anversoEnviable && !!reversoEnviable && anversoStatus === 'read' && reversoStatus !== 'processing' && !extrayendo;
    if (!leidas) return null;
    // El MRZ que lee el hook aparte (con el auxilio de 180°) también cuenta, si la cascada no lo leyó.
    const delDorso = hallazgosReverso.some((h) => h.origen === 'mrz')
      ? hallazgosReverso
      : [...hallazgosReverso, ...hallazgosDelMrz(mrz)];
    return aceptarCaras(hallazgosAnverso, delDorso, { exigirConfirmacion: mismoDniDebeConfirmarse() });
  }, [anversoEnviable, reversoEnviable, anversoStatus, reversoStatus, extrayendo, hallazgosAnverso, hallazgosReverso, mrz]);

  /** Los cuatro datos que un empleado de SADAIC tiene que poder corroborar. */
  const sinLeerEnElFrente = useMemo(() => {
    const necesarios: [string, string][] = [
      ['apellido', T.rApellido],
      ['nombres', T.rNombre],
      ['numeroDocumento', T.rNumero],
      ['fechaNacimiento', T.rNacimiento],
    ];
    return necesarios.filter(([campo]) => !camposTotales[campo]?.valor).map(([, r]) => r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camposTotales, T]);

  /**
   * Entrega la foto. Devuelve si llegó al server. `sinAviso` lo usa la
   * validación en pasos: ahí la calidad y lo que no se leyó ya se mostraron en
   * cada foto, y pedir un segundo toque a quien ya confirmó sus datos sobra.
   */
  const handleSend = async (opciones?: { sinAviso?: boolean }): Promise<boolean> => {
    if (!anversoEnviable || !reversoEnviable || !destino) return false;
    const calidad = [
      ...motivosDeCalidad(calidadAnverso),
      ...motivosDeCalidad(calidadReverso),
    ].filter((m, i, todos) => todos.indexOf(m) === i);
    if (!opciones?.sinAviso && (sinLeerEnElFrente.length > 0 || calidad.length > 0) && !avisoEnvio) {
      setAvisoEnvio({ faltan: sinLeerEnElFrente, calidad });
      return false;
    }
    // El código 2D SIN tapar, recortado de la foto original del frente, para
    // que el server lo lea (KAN-450): es lo que prueba nombres, número,
    // nacimiento y sexo. Lleva el número de trámite: por eso la foto que se
    // entrega lo sigue teniendo tapado, y este recorte vive lo que dura el
    // pedido. Sin el lugar del código, las zonas que tapa la plantilla. El
    // formulario interno no lo manda: esa ruta no lee.
    const zonasDelCodigo =
      zonasSensiblesAnverso.length > 0 ? zonasSensiblesAnverso : tapadoFrente.zonas.map((z) => z.caja);
    let codigoJpegB64: string | null = null;
    if (destino.tipo === 'publico' && frente && zonasDelCodigo.length > 0) {
      try {
        codigoJpegB64 = recortarCodigo(frente.canvas, zonasDelCodigo);
      } catch {
        codigoJpegB64 = null;
      }
    }
    setSending(true);
    setSendError('');
    const corte = new AbortController();
    const plazo = setTimeout(() => corte.abort(), PLAZO_DE_ENTREGA_MS);
    try {
      const { url, credencial, cabeceras } = await destinoHttp(destino);
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...cabeceras },
        signal: corte.signal,
        body: JSON.stringify({
          ...credencial,
          // UNA imagen con las dos caras, el frente arriba y el dorso abajo:
          // quien la recibe abre un archivo y ve el documento entero.
          documentoJpegB64: canvasToJpegBase64(apilarCaras(anversoEnviable, reversoEnviable)),
          ...(codigoJpegB64 ? { codigoJpegB64 } : {}),
          // Lo que viaja al server son las líneas canónicas reconstruidas, no el
          // texto crudo del OCR: el server las vuelve a verificar contra la base.
          mrzLines: mrz ? [mrz.raw1, mrz.raw2, mrz.raw3] : undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setSendError(mensajeDelServidor(data?.error, T));
        return false;
      }
      setEntregada(firmaDeLaEntrega);
      onUploaded();
      // NO se cierra solo. Cerrar era esconder el acuse: la persona apretaba
      // enviar, la ventana desaparecía y no quedaba ninguna señal de que la
      // foto había llegado. El acuse queda a la vista y cierra quien envió.
      return true;
    } catch {
      // También el corte por el plazo: el pedido rechaza con AbortError.
      setSendError(T.upload_failed);
      return false;
    } finally {
      clearTimeout(plazo);
      setSending(false);
    }
  };

  return {
    ACCIONABLES,
    aceptacion,
    aceptarCorte,
    anversoEnviable,
    anversoError,
    anversoStatus,
    avisoEnvio,
    calidadAnverso,
    calidadReverso,
    camara,
    camposTotales,
    canSend,
    cancelar,
    completado,
    completar,
    copiarSiDifiere,
    cuitDelDocumento,
    datos,
    domicilio,
    enviado,
    estaVerificado,
    extrayendo,
    faltanDelFrente,
    handleFoto,
    caraEquivocada,
    handleSend,
    hayMarcaNegra,
    queTapaLaMarca,
    marcaPuesta,
    ponerMarca,
    incluirDomicilio,
    marcas,
    motivosDeCalidad,
    mrz,
    nacionalidadConfirmada,
    nombreDeFila,
    onPreviewDown,
    onPreviewUp,
    previewAnversoRef,
    previewReversoRef,
    procesando,
    propuesta,
    propuestaRef,
    reasonMsg,
    rechazarCorte,
    refreshPreview,
    renglonesAnverso,
    reversoEnviable,
    reversoError,
    reversoStatus,
    revisionCuit,
    sendError,
    sending,
    setCamara,
    setIncluirDomicilio,
    setTapadoAutomatico,
    sexoConfirmado,
    sinLeerEnElFrente,
    tapadoAutomatico,
    tapadoDorso,
    tapadoFrente,
    textoDe,
    tramiteTapadoFrente,
    usarComoLaOtraCara,
    intercambiarCaras,
    valoresLeidos,
    vencido,
    vencimiento,
    verdictAnverso,
    verdictDorso,
    vieneAlCaso,
    zonasSensiblesAnverso,
  };
}

/** Una lectura que dejó de ser la vigente (otra foto, o «Cancelar»): su resultado no entra. */
class LecturaDescartada extends Error {}

/** Todo lo que la lectura del DNI le expone a quien la dibuja. */
export type LecturaDni = ReturnType<typeof useLecturaDni>;
